import Modal from "common/Modal/Modal";
import { OrderTypes } from "@shared/features/trades/common/types";
import React, { useContext } from "react";
import Flex from "common/Boxes/Flex";
import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import {
  ICON_TYPES,
  ORDER_TYPE_TO_TITLE_DISPLAY,
} from "@shared/common/constants";
import OrdersTable from "features/tradingTerminal/Orders/OrdersTable";
import styled, { useTheme } from "styled-components";
import {
  Arrow,
  ConfirmButton,
  FormDivider,
  SummaryFieldTitle,
} from "features/tradingTerminal/common/styles";
import Col from "antd/es/col";
import Row from "antd/es/row";
import Space from "antd/es/space";
import Link from "common/Typography/Link";
import Button from "common/Buttons/Button";
import { typography } from "@shared/common/Themes/typography";
import IconWithFallback from "Global/IconWithFallback";
import Tag from "common/Tags/Tag";
import ExistingOrderTable from "features/tradingTerminal/Orders/ExistingOrderTable";
import TrailingSummaryRow from "features/tradingTerminal/Summary/TrailingSummaryRow";
import useSummaryModal, {
  EXISTING_AMOUNT_TITLE,
} from "@shared/features/tradingTerminal/common/hooks/useSummaryModal";
import Card from "common/Boxes/Card";
import ExpandCollapse from "common/Animations/ExpandCollapse";
import PlusOutlined from "@ant-design/icons/PlusOutlined";
import { useGetTooltipConfigForField } from "features/configuration/GeneralConfiguration/ConfigurationItem/ConfigurationItemTooltipsConfig";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import {
  confirmWithCallback,
  displayResultMessage,
  openMessage,
} from "utils/misc";
import { useHistory } from "react-router-dom";
import {
  ADMIN_SIGNALS_PATH,
  MOBILE_SEARCH,
  OPEN_TRADES_PATH,
} from "common/constants";
import useQuotaModal from "features/settings/Quota/useQuotaModal";
import { UsageType, WorkspaceType, LanguageNameSpaces } from "@shared/types";
import Drawer from "common/Drawers/Drawer";
import Tabs from "common/Tabs/Tabs";
import useMedia from "utils/hooks/useMedia";
import { TradingTerminalContext } from "@shared/features/tradingTerminal/TradingTerminal.context.tsx";
import { appendSubGroupId } from "utils/routes.tsx";
import { selectWorkspace } from "@shared/slices/common.slice.ts";
import { useSelector } from "react-redux";
import { SIGNAL_BOTS_SIGNALS_PATH } from "features/bots/common/constants.ts";
import { CANCEL_ON_PROFIT_BEFORE_ENTRY_TITLE } from "@shared/features/tradingTerminal/common/constants.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useExchangesMetadata } from "@shared/utils/hooks/misc";
import { shouldCheckCreateTradeQuota } from "features/tradingTerminal/Summary/SummaryModal.utils";

interface SummaryModalProps {
  visible: boolean;
  closeModal: () => void;
  isConfirm?: boolean;
}

const SummaryModal = ({
  visible,
  closeModal,
  isConfirm,
}: SummaryModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADING_TERMINAL);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const isSmallMobile = useMedia(
    [`(max-width: ${MOBILE_SEARCH})`],
    [true],
    false
  );
  const theme = useTheme();
  const { getHelperTooltipProps } = useGetTooltipConfigForField();
  const { getIsFree } = useExchangesMetadata();

  const history = useHistory();
  const quotaCheckData = useQuotaModal({
    usageType: UsageType.MANUAL_TRADE,
  });
  const [confirmModal, confirmModalContextHolder] = Modal.useModal();

  const {
    symbols,
    account,
    ordersErrors,
    expanded,
    setExpanded,
    orders,
    useOrders,
    generalDisplayData,
    onSubmit,
    createTradeLoading,
    createSignalLoading,
    showZeroRatioTargetsWarning,
    isMobile,
    mobileSummaryTab,
    setMobileSummaryTab,
    createTradeButtonDisabled,
    getShowOrders,
    onAddOrderClick,
    shouldDisplayField,
  } = useSummaryModal(closeModal);
  const { isSignal, ignoreOrdersRatios, subGroupIds } = useContext(
    TradingTerminalContext
  )!;
  const workspace = useSelector(selectWorkspace);
  const isAdminWorkspace = workspace === WorkspaceType.ADMIN;

  const onTradeOrSignalCreated = () => {
    if (isSignal) {
      if (isAdminWorkspace) {
        history.push(ADMIN_SIGNALS_PATH);
      } else {
        const subGroupId = subGroupIds?.[0];
        let nextPath = SIGNAL_BOTS_SIGNALS_PATH;
        if (subGroupId) {
          nextPath = appendSubGroupId(
            SIGNAL_BOTS_SIGNALS_PATH,
            subGroupIds?.[0]
          );
        }
        history.push(nextPath);
      }
    } else {
      history.push(OPEN_TRADES_PATH);
    }
  };

  const handleCreateTradeClick = () => {
    if (!shouldCheckCreateTradeQuota(isSignal, getIsFree(account?.exchange))) {
      onSubmit(displayResultMessage, onTradeOrSignalCreated);
      return;
    }
    quotaCheckData.handleTriggerClick().then((isQuotaError) => {
      if (!isQuotaError) {
        onSubmit(displayResultMessage, onTradeOrSignalCreated);
      }
    });
  };

  const onSubmitWithConfirm = async () => {
    if (createTradeButtonDisabled) {
      openMessage(t("messages.errors.resolve_errors"), "warning");
    } else if (showZeroRatioTargetsWarning) {
      const okPressed = await confirmWithCallback(
        confirmModal,
        t("modals.confirm_targets_with_zero"),
        t("buttons.create_trade"),
        false,
        t("buttons.yes"),
        t("buttons.no")
      ).catch(() => {});
      if (okPressed) handleCreateTradeClick();
    } else {
      handleCreateTradeClick();
    }
  };

  const generalItemsTitleToValue: Record<string, string | React.JSX.Element> = {
    [t("labels.amount")]: generalDisplayData.amount,
    [t(EXISTING_AMOUNT_TITLE)]: generalDisplayData.quantity,
    [t(isSignal ? "labels.signal_type" : "labels.trade_type")]:
      generalDisplayData.tradeType,
    [t(CANCEL_ON_PROFIT_BEFORE_ENTRY_TITLE)]:
      generalDisplayData.cancelTradeOnFirstTp,
    [t("labels.leverage")]: (
      <LeverageTag color={theme.colors.primaryBlue}>
        {generalDisplayData.direction}
        {generalDisplayData.margin}
      </LeverageTag>
    ),
  };

  const generalSummary = theme.isNewLook ? (
    <NewLookGeneral>
      <Flex $justifyContent="flex-start" style={{ gap: 12 }}>
        <IconWithFallback
          iconType={ICON_TYPES.COIN}
          coinOrExchangeName={symbols[0]}
          iconStyle={{ width: 40, height: 40 }}
        />
        <div style={{ minWidth: 0 }}>
          <NewLookSymbol>{symbols?.[0]}</NewLookSymbol>
          <NewLookAccount>{account?.client_name}</NewLookAccount>
        </div>
      </Flex>
      <NewLookDetails>
        {Object.entries(generalItemsTitleToValue).map(
          ([title, value], index) => {
            if (shouldDisplayField(title))
              return (
                <NewLookDetailRow key={index}>
                  <NewLookDetailLabel>{title}</NewLookDetailLabel>
                  <NewLookDetailValue>{value}</NewLookDetailValue>
                </NewLookDetailRow>
              );
          }
        )}
      </NewLookDetails>
    </NewLookGeneral>
  ) : (
    <div>
      <Flex $justifyContent="flex-start">
        <IconWithFallback
          iconType={ICON_TYPES.COIN}
          coinOrExchangeName={symbols[0]}
          iconStyle={{ width: 46, height: 46, marginRight: 12 }}
        />
        <div>
          <Title level={2}>{symbols?.[0]}</Title>
          <Title level={3} type="secondary">
            {account?.client_name}
          </Title>
        </div>
      </Flex>
      <FormDivider style={{ margin: "12px 0" }} />
      <Space
        orientation="vertical"
        size={isMobile ? 16 : 24}
        style={{ width: "100%" }}
      >
        {Object.entries(generalItemsTitleToValue).map(
          ([title, value], index) => {
            if (shouldDisplayField(title))
              return (
                <div key={index}>
                  <SummaryFieldTitle>{title}</SummaryFieldTitle>
                  {typeof value === "string" ? <Text>{value}</Text> : value}
                </div>
              );
          }
        )}
      </Space>
    </div>
  );

  const getAddOrderButton = (orderType: OrderTypes) => (
    <Link onClick={() => onAddOrderClick(orderType)}>
      <PlusOutlined style={{ padding: "0 4px 0 12px" }} />
      {t("buttons.add_order")}
    </Link>
  );

  const getOrdersTable = (orderType: OrderTypes) => (
    <>
      <ExistingOrderTable orderType={orderType} />
      {!!orders[orderType].length && (
        <OrdersTable
          orderType={orderType}
          orders={orders[orderType]}
          ordersErrors={ordersErrors?.[orderType]}
          useOrders={useOrders[orderType]}
          readonly={true}
          spacing={4}
          ignoreRatios={ignoreOrdersRatios[orderType]}
        />
      )}
      {orderType === OrderTypes.STOP && !isSignal && (
        <div style={{ marginTop: 16 }}>
          <SummaryFieldTitle>{t("labels.stop_timeout")}</SummaryFieldTitle>
          <Text>{generalDisplayData.stopTimeout}</Text>
        </div>
      )}
    </>
  );

  const getTrailing = (orderType: OrderTypes) => (
    <>
      <FormDivider style={{ margin: "16px 0" }} />
      <TrailingSummaryRow orderType={orderType} />
    </>
  );

  const getOrdersSummary = (orderType: OrderTypes) => {
    const tooltipProps = getHelperTooltipProps(orderType);
    const showOrders = getShowOrders(orderType);
    return (
      <OrdersSummaryCard key={orderType}>
        <ExpandCollapseTitle
          onClick={
            showOrders
              ? () =>
                  setExpanded((prevState) => ({
                    ...prevState,
                    [orderType]: !expanded[orderType],
                  }))
              : undefined
          }
        >
          <Flex $justifyContent="flex-start">
            <Title level={3}>
              {t(ORDER_TYPE_TO_TITLE_DISPLAY[orderType])}{" "}
              {orderType === OrderTypes.STOP && tooltipProps && (
                <HelperTooltip
                  {...tooltipProps}
                  description={tCommon(tooltipProps.description)}
                  title={tCommon(tooltipProps.title)}
                />
              )}
            </Title>

            {!showOrders && getAddOrderButton(orderType)}
          </Flex>
          {showOrders && <Arrow $expanded={expanded[orderType]} />}
        </ExpandCollapseTitle>
        {showOrders && (
          <ExpandCollapse expanded={expanded[orderType]}>
            <div style={{ marginTop: 12 }}>{getOrdersTable(orderType)}</div>
          </ExpandCollapse>
        )}
        {getTrailing(orderType)}
      </OrdersSummaryCard>
    );
  };

  const ordersSummaryCards = Object.values(OrderTypes).map((orderType) =>
    getOrdersSummary(orderType)
  );

  const createTradeButton = (
    <ConfirmButton
      type="primary"
      loading={
        createTradeLoading || createSignalLoading || quotaCheckData.isLoading
      }
      onClick={onSubmitWithConfirm}
      style={{
        marginLeft: isMobile ? 0 : 16,
        width: isMobile ? "100%" : "auto",
      }}
      $disabled={createTradeButtonDisabled}
    >
      {t(isSignal ? "buttons.create_signal" : "buttons.create_trade")}
    </ConfirmButton>
  );
  const footer = (
    <Flex $justifyContent="flex-end" style={{ padding: "10px 16px" }}>
      {theme.isNewLook ? (
        // New look: the secondary action is a quiet pill next to the primary.
        <Button size="large" onClick={closeModal}>
          {t(isConfirm ? "buttons.cancel" : "buttons.close")}
        </Button>
      ) : (
        <Link onClick={closeModal} style={{ fontSize: typography.fontSizes.L }}>
          {t(isConfirm ? "buttons.cancel" : "buttons.close")}
        </Link>
      )}
      {isConfirm && createTradeButton}
    </Flex>
  );

  const tabsItems = Object.values(OrderTypes).map((orderType) => {
    const showOrders = getShowOrders(orderType);
    return {
      key: orderType,
      label: t(ORDER_TYPE_TO_TITLE_DISPLAY[orderType]),
      children: (
        <>
          {showOrders
            ? getOrdersTable(orderType)
            : getAddOrderButton(orderType)}
          {getTrailing(orderType)}
        </>
      ),
    };
  });

  if (isMobile) {
    return (
      <StyledDrawer open={visible} onClose={closeModal}>
        <DrawerContent>
          <DrawerInnerContent $isSmallMobile={isSmallMobile}>
            {generalSummary}
            <Tabs
              defaultActiveKey={mobileSummaryTab}
              onChange={(value) => setMobileSummaryTab(value as OrderTypes)}
              style={{ marginTop: 24 }}
              items={tabsItems}
            />
          </DrawerInnerContent>
          <DrawerBottomSection>
            <FormDivider style={{ margin: "8px 0 0" }} />
            <div style={{ padding: 16 }}>{createTradeButton}</div>
          </DrawerBottomSection>
        </DrawerContent>
      </StyledDrawer>
    );
  }

  return (
    <>
      <Modal
        title={t(isConfirm ? "modals.confirm_trade" : "modals.summary")}
        open={visible}
        onCancel={closeModal}
        footer={footer}
        width={840}
        styles={{
          body: {
            maxHeight: "min(600px, calc(100vh - 100px))",
            overflowY: "auto",
            overflowX: "hidden",
            padding: "24px 32px",
          },
        }}
        centered={true}
      >
        <Row gutter={theme.isNewLook ? 24 : 60}>
          <Col span={10}>{generalSummary}</Col>
          <Col span={14}>
            <Space
              orientation="vertical"
              size={theme.isNewLook ? 12 : 24}
              style={{ width: "100%" }}
            >
              {ordersSummaryCards}
            </Space>
          </Col>
        </Row>
      </Modal>
      {confirmModalContextHolder}
      {quotaCheckData.quotaModal}
    </>
  );
};

export default SummaryModal;

const OrdersSummaryCard = styled(Card)`
  background: ${(p) => p.theme.colors.boxInCardColor};
  box-shadow: none;
  ${(p) =>
    p.theme.isNewLook &&
    `
    background: ${p.theme.colors.cardBgColor};
    box-shadow: inset 0 0 0 1px ${p.theme.colors.cardBorderColor};
    border-radius: 14px;
    padding: 16px 18px;
    h3 {
      font-size: 15px;
      font-weight: ${p.theme.typography.fontWeights.SEMI_BOLD};
      color: ${p.theme.colors.titleColor};
    }
  `}
`;
const NewLookGeneral = styled.div`
  padding: 18px;
  border-radius: 14px;
  background: ${(p) => p.theme.colors.cardLightBgColor};
  border: ${(p) => p.theme.borders.card};
`;
const NewLookSymbol = styled.div`
  color: ${(p) => p.theme.colors.titleColor};
  font-size: 17px;
  line-height: 1.3;
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
`;
const NewLookAccount = styled.div`
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const NewLookDetails = styled.div`
  margin-top: 14px;
  border-top: ${(p) => p.theme.borders.lightest};
`;
const NewLookDetailRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 11px 0;
  &:not(:last-child) {
    border-bottom: ${(p) => p.theme.borders.lightest};
  }
`;
const NewLookDetailLabel = styled.div`
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 13px;
`;
const NewLookDetailValue = styled.div`
  color: ${(p) => p.theme.colors.titleColor};
  font-size: 13.5px;
  font-variant-numeric: tabular-nums;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  text-align: right;
`;
const LeverageTag = styled(Tag)`
  width: fit-content;
  font-size: ${(p) => p.theme.typography.fontSizes.M};
  font-weight: ${(p) => p.theme.typography.fontWeights.NORMAL};
`;
const StyledDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 0 !important;
    overflow: hidden !important;
  }
`;
const DrawerContent = styled(Flex)`
  flex-direction: column;
  width: 100%;
  height: 100%;
  position: relative;
`;
const DrawerInnerContent = styled.div<{ $isSmallMobile: boolean }>`
  width: 100%;
  padding: ${(p) => (p.$isSmallMobile ? "16px 16px 100px" : "24px 24px 100px")};
  overflow: auto;
  ${(p) => p.theme.isNewLook && `padding-top: 48px;`}
`;
const DrawerBottomSection = styled.div`
  width: 100%;
  position: absolute;
  bottom: 0;
  background: ${(p) => p.theme.colors.layoutBgColor};
`;
const ExpandCollapseTitle = styled(Flex)`
  ${(p) =>
    p.onClick &&
    `
    cursor: pointer;
  `};
`;
